import { useEffect, useRef, useState } from 'react'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'
import { SUPPORTED_LANGUAGES, type AppLanguage } from '../../i18n/languages'

type LanguagePickerProps = {
  value: AppLanguage
  onChange: (language: AppLanguage) => void
  disabled?: boolean
  compact?: boolean
}

const LanguagePicker = ({ value, onChange, disabled = false, compact = false }: LanguagePickerProps) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const active = SUPPORTED_LANGUAGES.find((language) => language.code === value)

  useEffect(() => {
    if (!open) return
    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [open])

  const closedClass = compact
    ? 'border-kilo-line bg-kilo-alt/60 text-kilo-soft hover:border-kilo-accent/30'
    : `${KILO.border.default} bg-kilo-card text-kilo-soft hover:border-kilo-accent/30`

  const openClass = 'border-kilo-accent/40 bg-kilo-accent/10 text-kilo-text'

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        className={
          compact
            ? `flex h-10 w-full items-center justify-between gap-2 rounded-full border px-3 text-xs font-medium transition ${open ? openClass : closedClass}`
            : `flex h-12 w-full items-center justify-between gap-3 rounded-xl border px-4 text-left text-sm transition ${open ? openClass : closedClass}`
        }
      >
        <span>{active?.nativeLabel ?? value}</span>
        <MaterialIcon name={open ? 'expand_less' : 'expand_more'} size="sm" />
      </button>

      {open ? (
        <div className={`absolute left-0 right-0 z-30 mt-2 overflow-hidden rounded-xl border border-kilo-border bg-kilo-page shadow-xl`}>
          {SUPPORTED_LANGUAGES.map((language) => {
            const selected = value === language.code
            return (
              <button
                key={language.code}
                type="button"
                disabled={disabled}
                onClick={() => {
                  onChange(language.code)
                  setOpen(false)
                }}
                className={`flex w-full items-center justify-between px-4 py-3 text-left text-sm transition ${
                  selected ? 'bg-kilo-accent/10 text-kilo-text' : `${KILO.text.soft} hover:bg-kilo-card`
                }`}
              >
                <span>{language.nativeLabel}</span>
                <span className={`text-xs ${KILO.text.muted}`}>{language.label}</span>
              </button>
            )
          })}
        </div>
      ) : null}
    </div>
  )
}

export default LanguagePicker
